import React, { useState } from 'react';
import { compact } from 'lodash';
import { useHistory } from 'react-router';
import { Page, Tabs, Typography, Divider } from 'seagull';
import { routes } from "../../constants";
import { selectors } from "../../store/state-selectors";
import RestfulWrapper from "../../components/RestfulWrapper/RestfulWrapper";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import { useStore, useStoreActions } from "../../contexts";
import { getHelpArticle, numberAndUnit } from "../../infra/utils";
import { NotificationSettingsTable } from "./NotificationSettingsTable";
import { Tab, Table, isDays, minutesToDays, getRecipientTypesText, reminderColumnDefsMap, reminderHeaderItemsMap, notificationColumnDefs, notificationHeaderItems } from "./NotificationSettings.utils";
import styles from "./NotificationSettings.module.scss";
const NotificationSettings = () => {
  const {
    api
  } = useStoreActions();
  const history = useHistory();
  const businessId = useStore(selectors.user.businessId);
  const [selectedTab, setSelectedTab] = useState(Tab.Vendor);
  const fetchData = async () => {
    if (!businessId) {
      history.push(routes.home);
      return {};
    }
    return api.notification.getNotificationSettings(businessId);
  };
  return /*#__PURE__*/React.createElement(Page, {
    header: "Notification Settings",
    subheader: /*#__PURE__*/React.createElement(React.Fragment, null, "Set which notifications you and your clients will receive.", /*#__PURE__*/React.createElement(ExternalLinkButton, {
      href: getHelpArticle('9729494-notification-settings'),
      "data-testid": 'learn-more-link',
      openInNewTab: true,
      className: styles.learnMoreLink
    }, "Learn more")),
    "data-testid": "notification-settings"
  }, /*#__PURE__*/React.createElement(Tabs, {
    value: selectedTab,
    onChange: (_, value) => setSelectedTab(value),
    tabs: [{
      component: 'Notifications sent to your team',
      id: Tab.Vendor,
      value: Tab.Vendor,
      testId: "vendor-notifications-tab"
    }, {
      component: 'Notifications sent to your clients',
      id: Tab.Client,
      value: Tab.Client,
      testId: "client-notifications-tab"
    }]
  }), /*#__PURE__*/React.createElement(RestfulWrapper, {
    fetch: fetchData,
    renderChild: _ref => {
      let {
        data,
        refresh
      } = _ref;
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Notifications, "-table-title"),
        variant: "h6",
        className: styles.tableTitle
      }, 'Notifications triggered when an event occurs'), /*#__PURE__*/React.createElement(NotificationSettingsTable, {
        data: data,
        refresh: refresh,
        tableType: Table.Notifications,
        columnDefs: notificationColumnDefs,
        headerItems: notificationHeaderItems,
        selectedTab: selectedTab,
        getRowItems: _ref2 => {
          let {
            notification,
            text,
            key
          } = _ref2;
          return [{
            value: /*#__PURE__*/React.createElement(Typography, {
              weight: "bold"
            }, text),
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-title")
          }, {
            value: notification.recipientTypes.length ? 'Active' : 'Inactive',
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-status")
          }, {
            value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/React.createElement(Typography, {
              key: text,
              as: "div"
            }, text)),
            'data-testid': "".concat(Table.Notifications, "-row-").concat(key, "-notified-team-members")
          }];
        }
      }), /*#__PURE__*/React.createElement(Divider, null), /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Reminders, "-table-title"),
        variant: "h6",
        className: styles.tableTitle
      }, 'Notifications triggered when an action is delayed'), selectedTab === Tab.Client && /*#__PURE__*/React.createElement(Typography, {
        "data-testid": "".concat(Table.Reminders, "-subtitle"),
        skin: "secondary",
        variant: "paragraph1"
      }, 'Notifications are sent at fixed intervals, up to a selected number of retries'), /*#__PURE__*/React.createElement(NotificationSettingsTable, {
        data: data,
        refresh: refresh,
        tableType: Table.Reminders,
        columnDefs: reminderColumnDefsMap[selectedTab],
        headerItems: reminderHeaderItemsMap[selectedTab],
        selectedTab: selectedTab,
        getRowItems: _ref3 => {
          let {
            notification,
            text,
            key
          } = _ref3;
          return compact([{
            value: /*#__PURE__*/React.createElement(Typography, {
              weight: "bold"
            }, text),
            'data-testid': "reminders-row-".concat(key, "-title")
          }, {
            value: notification.recipientTypes.length ? 'Active' : 'Inactive',
            'data-testid': "reminders-row-".concat(key, "-status")
          }, {
            value: notification.recipientTypes.length ? isDays(notification.recurring.minutes) ? numberAndUnit(minutesToDays(notification.recurring.minutes), 'day') : numberAndUnit(notification.recurring.minutes, 'minute') : '',
            'data-testid': "reminders-row-".concat(key, "-").concat(selectedTab === Tab.Client ? 'interval' : 'delay')
          }, selectedTab === Tab.Client && {
            value: notification.recipientTypes.length ? notification.recurring.times : '',
            'data-testid': "reminders-row-".concat(key, "-retries")
          }, {
            value: getRecipientTypesText(notification.recipientTypes, key).map(text => /*#__PURE__*/React.createElement(Typography, {
              key: text,
              as: "div"
            }, text)),
            'data-testid': "reminders-row-".concat(key, "-notified-team-members")
          }]);
        }
      }));
    }
  }));
};
export default NotificationSettings;